/**
 * ユーザー情報の入力ページ
 */
import clsx from "clsx"
import { get } from "lodash"
import Link from "next/link"
import router from "next/router"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { updateMyUserInfo } from "src/models/user/updateMyUserInfo"
import { Auth as AuthMessage } from "src/config/messages"
import routes from "routes"
import { globalStoreContext } from "src/store/GlobalStore"
import * as regex from "src/utils/regex"

// hook
import { useApiUserData } from "src/components/hooks/useApiUserData"
import { useLoading } from "src/components/hooks/useLoading"
import { useTieupToast } from "src/components/hooks/useTieupToast"
import { getFirebaseUserFromCookie } from "src/utils/auth/userCookies"

// view
import DateSelector from "src/components/common/DateSelector"
import HTMLHead from "src/components/utils/HTMLHead"
import { DefaultLayoutForReader } from "src/components/common/layouts/DefaultLayoutForReader"
import InputPassword from "src/components/common/InputPassword"
import { useLoginConfirm } from "src/components/common/auth/useLoginConfirm"
import { useCommonConfirmDialog } from "src/components/hooks/useCommonConfirmDialog"
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth"
import { registerUser } from "../../models/user/registerUser"
import { registerNotificationMail } from "../../models/Notification/registerNotificationMail"

/** 入力完了フォーム */
const VerifyingView: React.FC = () => {
  return (
    <main>
      <header className="p-signIn-header">
        <h1 className="p-signIn-header-title">会員登録ありがとうございます</h1>
      </header>

      <section className="p-signIn-section">
        <div className="p-signIn-section-inner">
          <h2 className="p-signIn-section-title">
            通知用メールアドレスに
            <br />
            登録完了メールを送信しました。
          </h2>
          <p>
            ご登録いただいたメールアドレスに確認用メールを送信いたしました。
            <br />
            内容をご確認いただき、会員登録の完了をお願いいたします。
          </p>
          <p className="c-note-primary -normal">
            10分経っても届かない場合はinformation@tieupnovels.comからのメールの受信を許可をお願いいたします。
          </p>
          <p className="c-note-primary -normal">
            「【たいあっぷ】ユーザ情報登録のご案内」メール内のリンクの有効期限を過ぎた場合は、お手数ですが以下「通知用メールアドレスの変更はこちら」より再度メールアドレスをご登録ください。
          </p>
          <p className="c-note-primary -normal">
            <Link href={routes.authChangeNotificationEmail()}>
              通知用メールアドレスの変更はこちら
            </Link>
          </p>
        </div>
      </section>
    </main>
  )
}

type FormProps = {
  notificationEmail: string
  setNotificationEmail: (arg: string) => void
  setIsVerifying: (arg: boolean) => void
}

export type FormInputData = {
  display_name: string
  email: string
  password?: string
  birthdate: string
}

const INITIAL_BIRTH_DATE = "2000-01-01"

/** 入力フォーム */
const ProfileFormView: React.FC<FormProps> = ({
  notificationEmail,
  setNotificationEmail,
  setIsVerifying,
}) => {
  const { register, handleSubmit, errors, control, setValue } =
    useForm<FormInputData>()
  const [initialData, setInitialData] = useState({
    id: "",
    displayName: "",
  })
  const { globalDispatch } = useContext(globalStoreContext)
  const { fullScreenLoading } = useLoading()
  const { addToastError } = useTieupToast()
  const { updateApiUserData } = useApiUserData()
  const { loginConfirm } = useLoginConfirm()
  const { confirmDialog } = useCommonConfirmDialog()

  // 初期値をセット
  useEffect(() => {
    const user = getFirebaseUserFromCookie()
    const { id, email, displayName } = user

    // ソーシャルログインでない場合は初期値設定が不要
    if (user.id === null) return

    setInitialData({ id, displayName })
    setNotificationEmail(email)
  }, [setNotificationEmail])

  const onChangeEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation()
      setNotificationEmail(e.target.value)
    },
    [setNotificationEmail],
  )

  /** 送信 */
  const onSubmit = async (values: FormInputData) => {
    const auth = getAuth()
    const callLogin = async () => {
      try {
        fullScreenLoading(true)

        // 送信データを初期化
        const sendData = { ...values, name: "" }

        // デフォルトで、ユーザ名はユーザーIDと同値に設定しておく
        sendData.name = sendData.display_name

        // メールアドレス認証の場合 -> このタイミングで Firebase ユーザを作成、パスワードはフォームで入力する
        // ソーシャルログインの場合 -> すでに Firebase ユーザは作成済
        if (initialData.id === "" && values.password) {
          await createUserWithEmailAndPassword(
            auth,
            notificationEmail,
            values.password,
          )
        }

        // register
        await registerUser()
        // APIユーザの更新
        await updateMyUserInfo(sendData)
        // ストアの更新
        await updateApiUserData()
        // 通知用メールアドレス登録 & 認証メールの送信
        await registerNotificationMail(notificationEmail)

        globalDispatch({
          type: "user/hasActiveRequest",
          payload: true,
        })

        setIsVerifying(true)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)

        // Firebaseエラー
        if (e.code === "auth/email-already-in-use") {
          addToastError("このメールアドレスはすでに使用されています")
        } else if (e.code === "auth/invalid-email") {
          addToastError("メールアドレスの形式が正しくありません")
        }

        // APIエラー
        const errorKey = get(e, "response.data.data.key")
        if (errorKey === "ALREADY_REGISTER_DISPLAY_NAME") {
          addToastError("このユーザー名はすでに使用されています")

          // ユーザー登録に失敗したため、Firebase上に作成したユーザーを削除しておく
          const user = auth.currentUser
          await user?.delete()
        } else {
          addToastError("エラーが発生しました。最初からやり直してください。")
        }
      } finally {
        fullScreenLoading(false)
      }
    }

    loginConfirm(callLogin)
  }

  /** 会員登録を中断する */
  const stopRegistration = async () => {
    const auth = getAuth()
    confirmDialog(`本当に会員登録を中断しますか？`, "はい", "いいえ", () => {
      auth.signOut().then(() => {
        router.push(routes.authSignIn())
      })
    })
  }

  return (
    <main>
      <header className="p-signIn-header">
        <h1 className="p-signIn-header-title">新規会員登録</h1>
        <p className="p-signIn-header-note">
          ユーザー情報の登録を行います。新規会員登録を完了するためにはユーザー情報を登録してください。
        </p>
      </header>

      <section className="p-signIn-section">
        <div className="p-signIn-section-inner">
          <h2 className="p-signIn-section-title">ユーザー情報</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* ユーザID */}
            <div className="p-signIn-block">
              <label htmlFor="input-user-id" className="c-label-primary">
                ユーザーID
              </label>
              <input
                id="input-user-id"
                className={clsx({
                  "c-textfield-primary": true,
                  "-error": !!errors.display_name,
                })}
                type="text"
                name="display_name"
                ref={register({
                  minLength: 5,
                  maxLength: 32,
                  required: true,
                  pattern: regex.regDisplayName,
                })}
              />
              <p
                className={clsx({
                  "c-note-primary": true,
                  "-error": errors.display_name,
                })}
              >
                ※半角英数+半角アンダーバーで5文字以上32文字以下で設定ください。マイページのURLになります。
              </p>
            </div>

            {/* メールアドレス ／ 通知用メールアドレス */}
            <div className="p-signIn-block">
              <label htmlFor="input-email" className="c-label-primary">
                {initialData.id ? "通知用メールアドレス" : "メールアドレス"}
              </label>
              <input
                id="input-email"
                className={clsx({
                  "c-textfield-primary": true,
                })}
                type="email"
                name={"email"}
                ref={register({ required: true })}
                onChange={onChangeEmail}
                value={notificationEmail ? notificationEmail : ""}
              />
              {errors.email && (
                <p className="c-note-primary -error">
                  {AuthMessage.error.email}
                </p>
              )}
              {!initialData.id && (
                <p
                  className={clsx({
                    "c-note-primary": true,
                  })}
                >
                  {initialData.id
                    ? "※ログイン用のメールアドレスとなります。すでに登録されているメールアドレスは使用できません。"
                    : "※ログイン及び通知用のメールアドレスとなります。すでに登録されているメールアドレスは使用できません。"}
                </p>
              )}
              <p
                className={clsx({
                  "c-note-primary": true,
                })}
              >
                ※必ずご連絡のつくメールアドレスをご設定ください。
              </p>
            </div>

            {/* パスワード */}
            {initialData.id ? null : (
              <div className="p-signIn-block">
                <label className="c-label-primary">パスワード</label>
                <InputPassword errors={errors} register={register} />
                <p
                  className={clsx({
                    "c-note-primary": true,
                    "-error": !!errors.password,
                  })}
                >
                  {
                    "※英字・数字及び右記記号「!\"#$%&'()*+,-./:;<=>?@[]^_`{|}~」で10文字以上で設定ください。"
                  }
                </p>
                <p
                  className={clsx({
                    "c-note-primary": true,
                    "-error": !!errors.password,
                  })}
                >
                  ※英字、数字、記号それぞれ1文字以上含めてください。
                </p>
              </div>
            )}

            {/* 生年月日 */}
            <div className="p-signIn-block">
              <label className="c-label-primary">生年月日</label>
              <Controller
                name="birthdate"
                control={control}
                defaultValue={INITIAL_BIRTH_DATE}
                render={({ value }) => (
                  <DateSelector
                    selected={value ?? INITIAL_BIRTH_DATE}
                    onChange={(s: string) => {
                      setValue("birthdate", s)
                    }}
                  />
                )}
              />
              <p
                className={clsx({
                  "c-note-primary": true,
                })}
              >
                ※全体には公開されない情報です。
              </p>
            </div>

            {/* 登録ボタン */}
            <div className="p-button">
              <button className="c-button-secondary -full">登録</button>
            </div>
          </form>
          {initialData.id && (
            <>
              <div
                style={{
                  textAlign: "center",
                  marginTop: "60px",
                  paddingTop: "30px",
                  borderTop: "1px solid #d1d1d6",
                }}
              >
                <button
                  onClick={stopRegistration}
                  className="c-textButton-gray"
                >
                  会員登録を中断する
                </button>
                <p className="c-note-primary -normal">
                  新規会員登録を中断する場合は、こちらをクリックして下さい。
                </p>
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  )
}

// page
const CreateProfile: React.FC = () => {
  const {
    globalStore: {
      user: { hasActiveRequest, clientState },
    },
  } = useContext(globalStoreContext)

  // 認証中状態
  const [isVerifying, setIsVerifying] = useState(false)

  // 通知用メールアドレス
  const [notificationEmail, setNotificationEmail] = useState("")

  useEffect(() => {
    if (clientState === 2) {
      router.push(routes.mypage())
    }
  }, [clientState])

  useEffect(() => {
    if (hasActiveRequest >= 1) {
      setIsVerifying(hasActiveRequest === 2)
    }
  }, [hasActiveRequest])

  return (
    <DefaultLayoutForReader>
      <HTMLHead path="auth/create-profile" />
      {isVerifying ? (
        <VerifyingView />
      ) : (
        <ProfileFormView
          setNotificationEmail={setNotificationEmail}
          notificationEmail={notificationEmail}
          setIsVerifying={setIsVerifying}
        />
      )}
    </DefaultLayoutForReader>
  )
}

export default CreateProfile
